import { APIProvider, Map as GoogleMap } from "@vis.gl/react-google-maps";
import clsx from "clsx";
import { AnimatePresence, motion } from "motion/react";
import React, { Fragment, forwardRef, type JSX, useId, useMemo, useState } from "react";
import Supercluster from "supercluster";
import { Overlay } from "../overlay";
import { Portal } from "../portal";
import { defaultCamera } from "./constants";
import { MapControls, type View } from "./map-controls";
import { MapViewClusterLayer } from "./map-view-cluster-layer";
import { type MapViewHandle, MapViewImperativeHandle } from "./map-view-imperative-handle";
import { useMapStore } from "./store";

/** Props Interface */
export interface Marker {
	latitude: number;
	longitude: number;
	render?: () => JSX.Element;
	excludeFromClustering?: boolean;
}

interface MapViewProps {
	onTilesLoaded?: () => void;
	markers?: Marker[];
	apiKey: string;
	mapId?: string;
	useDefaultCenter?: boolean;
	defaultZoom?: number;
	disableClustering?: boolean;
	clusterMaxZoom?: number;
}

export const MapView = forwardRef<MapViewHandle, MapViewProps>(function MapView(
	{
		onTilesLoaded,
		markers,
		apiKey,
		mapId,
		useDefaultCenter,
		defaultZoom = defaultCamera.zoom,
		disableClustering: disableClusteringProp = false,
		clusterMaxZoom = 17,
	}: MapViewProps,
	ref,
) {
	const [view, setView] = useState<View>("default");
	const layoutId = useId();
	const store = useMapStore();
	const disableClustering = disableClusteringProp || store.disableClustering;

	const clusterIndex = useMemo(() => {
		if (!markers || disableClustering) return null;
		const points = markers
			.map((m, i) => ({ marker: m, index: i }))
			.filter(({ marker }) => !marker.excludeFromClustering)
			.map(({ marker, index }) => ({
				type: "Feature" as const,
				geometry: {
					type: "Point" as const,
					coordinates: [marker.longitude, marker.latitude],
				},
				properties: { index },
			}));
		return new Supercluster({
			radius: 60,
			maxZoom: clusterMaxZoom,
		}).load(points);
	}, [markers, disableClustering, clusterMaxZoom]);

	/** Map Component */
	const MapComponent = (
		<APIProvider apiKey={apiKey}>
			<GoogleMap
				style={{ width: "100%", height: "100%" }}
				gestureHandling="greedy"
				defaultCenter={
					useDefaultCenter
						? defaultCamera.center
						: markers
							? {
									lat: markers.at(0)?.latitude ?? defaultCamera.center.lat,
									lng: markers.at(0)?.longitude ?? defaultCamera.center.lng,
								}
							: defaultCamera.center
				}
				defaultZoom={defaultZoom}
				disableDefaultUI
				mapTypeId={store.mapType}
				renderingType="VECTOR"
				onTilesLoaded={onTilesLoaded}
				mapId={mapId}
			>
				<MapViewClusterLayer
					clusterIndex={clusterIndex}
					markers={markers}
					disableClustering={disableClustering}
					clusterMaxZoom={clusterMaxZoom}
				/>
				<MapViewImperativeHandle handleRef={ref} />
			</GoogleMap>
			<MapControls
				view={view}
				setView={setView}
			/>
		</APIProvider>
	);

	/** Render */
	return (
		<Fragment>
			<AnimatePresence mode="popLayout">
				<motion.div
					key={`map-${layoutId}`}
					className="relative w-full h-full cursor-grab active:cursor-grabbing"
					layoutId={layoutId}
				>
					{MapComponent}
				</motion.div>
				<Portal>
					{view === "maximized" && (
						<Fragment>
							<Overlay onClick={() => setView("default")} />
							<motion.div
								layoutDependency={view}
								key={`map-${layoutId}`}
								className={clsx(
									"z-20 fixed inset-8 cursor-grab active:cursor-grabbing",
									"rounded-lg overflow-hidden",
								)}
								layoutId={layoutId}
							>
								{MapComponent}
							</motion.div>
						</Fragment>
					)}
				</Portal>
			</AnimatePresence>
		</Fragment>
	);
});
